// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { Entitlement } from '@api/constants';
import { type EndpointGroup, SortOrder } from '@api/types';
import { hasEntitlement } from '@api/utils';
import { useSession } from '@common/authorization/context/Session';
import { withRole } from '@common/authorization/Roles';
import SearchInput from '@components/searchInput/SearchInput';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { useEmitOnError } from '@errorHandler/context/useEmitOnError';
import { type ApiError, AppError, isSessionError } from '@errorHandler/errors';
import { Alert, Button, Select } from '@imprivata-cloud/design-system';
import { useStatusAnnouncer } from '@portal/hooks/useStatusAnnouncer';
import { downloadAsFileToUserComputer } from '@utils/utils';
import { Checkbox, Spin, Table } from 'antd';
import { LogOut, Settings, Waypoints } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import GroupIcon from '../../../assets/icons/group.svg?react';
import { FILES_DOWNLOAD_NAMES, ROLE_TYPE_ADMIN } from '../../constants';
import { AssignGroupDialog, ManageGroupsDialog } from './dialogs/EndpointGroupDialogs';
import { AgentInstallerDrawer } from './drawers/AgentInstallerDrawer';
import { EndpointBehaviorDrawer } from './drawers/EndpointBehaviorDrawer';
import { buildColumns, buildRowSelection } from './EndpointsColumns';
import { EndpointsEmpty, EndpointsNoResults } from './empty/EndpointsEmptyStates';
import { useAgentVersionReport } from './hooks/useAgentVersionReport';
import {
  type AssignOutcome,
  useEndpointGroups,
  useSessionErrorEscalation,
} from './hooks/useEndpointGroups';
import { useEndpointsList } from './hooks/useEndpointsList';
import {
  ALL_GROUPS,
  type GroupFilter,
  type GroupRef,
  isSameGroupFilter,
  matchesGroupFilter,
} from './utils/endpointGroups';
import { toEndpointRow } from './utils/toEndpointRow';
import './Endpoints.less';

const TRANSLATION_ROOT = 'portal.settings.endpoints';
const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 100;
// The toolbar checkbox selects at most this many of the listed rows; more are checked by row.
const SELECT_ALL_MAX = 50;
const HOSTNAME_FIELD = 'hostname';
const CREATED_AT_FIELD = 'created_at';
const VERSION_FIELD = 'version';

type SortKey =
  | 'name'
  | 'most-up-to-date'
  | 'least-up-to-date'
  | 'most-recently-installed'
  | 'oldest-installed';

const SORT_FIELD_MAP: Record<SortKey, { field: string; order: SortOrder }> = {
  name: { field: HOSTNAME_FIELD, order: SortOrder.ASC },
  'most-up-to-date': { field: VERSION_FIELD, order: SortOrder.DESC },
  'least-up-to-date': { field: VERSION_FIELD, order: SortOrder.ASC },
  'most-recently-installed': { field: CREATED_AT_FIELD, order: SortOrder.DESC },
  'oldest-installed': { field: CREATED_AT_FIELD, order: SortOrder.ASC },
};

type OverlayKind = 'installer' | 'behavior' | 'manage-groups' | 'assign' | null;

type GroupFilterOption = { key: string; label: string; filter: GroupFilter };

/** The group filter applies to loaded rows only; a deleted group's filter reads as All groups. */
const useGroupFilter = (groups: readonly EndpointGroup[] | undefined) => {
  const { t } = useTranslation();
  const [groupFilter, setGroupFilter] = useState<GroupFilter>(ALL_GROUPS);

  const manualGroups = useMemo(
    () =>
      new Map<string, GroupRef>(
        (groups ?? []).map(({ groupId, displayName }) => [groupId, { groupId, displayName }]),
      ),
    [groups],
  );

  const activeFilter =
    groupFilter.kind === 'group' && !manualGroups.has(groupFilter.groupId)
      ? ALL_GROUPS
      : groupFilter;

  const filterOptions: GroupFilterOption[] = [
    { key: 'all', label: t(`${TRANSLATION_ROOT}.groups.filter.all`), filter: ALL_GROUPS },
    ...[...manualGroups.values()]
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
      .map(({ groupId, displayName }) => ({
        key: `group-${groupId}`,
        label: displayName,
        filter: { kind: 'group', groupId } as const,
      })),
    // Last, below a divider (Figma); the divider is drawn in Endpoints.less.
    { key: 'none', label: t(`${TRANSLATION_ROOT}.groups.filter.none`), filter: { kind: 'none' } },
  ];
  const activeFilterKey =
    filterOptions.find(option => isSameGroupFilter(option.filter, activeFilter))?.key ?? 'all';
  const selectFilter = (key: string | null) =>
    setGroupFilter(filterOptions.find(option => option.key === key)?.filter ?? ALL_GROUPS);

  return { manualGroups, activeFilter, filterOptions, activeFilterKey, selectFilter };
};

type ListProgressInput = {
  activeFilter: GroupFilter;
  searchPattern: string;
  groups: readonly EndpointGroup[] | undefined;
  loadedMatches: number;
  totalCount: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isEmpty: boolean;
};

/**
 * The group filter only sees loaded rows, so with no loaded matches the list keeps loading pages
 * until one matches or none are left, rather than showing an empty state early. A manual group's
 * memberCount is its true total, so loading stops once that many are loaded and the count shows
 * it; otherwise only the loaded matches are known.
 */
const listProgress = ({
  activeFilter,
  searchPattern,
  groups,
  loadedMatches,
  totalCount,
  hasNextPage,
  isFetchingNextPage,
  isEmpty,
}: ListProgressInput) => {
  const filteredGroup =
    activeFilter.kind === 'group' && searchPattern.length === 0
      ? groups?.find(group => group.groupId === activeFilter.groupId)
      : undefined;
  const morePages = hasNextPage && !(filteredGroup && loadedMatches >= filteredGroup.memberCount);
  const shownCount =
    activeFilter.kind === 'all' ? totalCount : (filteredGroup?.memberCount ?? loadedMatches);
  return {
    morePages,
    shownCount,
    showEmptyState: isEmpty && !morePages,
    // An empty list that is still looking for matches spins where the rows would be.
    spinning: isFetchingNextPage || isEmpty,
  };
};

type SelectionBarProps = {
  count: number;
  total: number;
  removing: boolean;
  disabled: boolean;
  onAssign: () => void;
  onRemove: () => void;
  onDeselect: () => void;
};

type FailedAssign = Extract<AssignOutcome, { kind: 'failed' }>;

const SelectionBar = ({
  count,
  total,
  removing,
  disabled,
  onAssign,
  onRemove,
  onDeselect,
}: SelectionBarProps) => {
  const { t } = useTranslation();
  const label = t(`${TRANSLATION_ROOT}.selection.count`, { count, total });
  return (
    <fieldset className="endpoints-selection-bar" aria-label={label}>
      <span className="endpoints-selection-count">{label}</span>
      <Button
        className="endpoints-selection-action"
        variant="default"
        size="sm"
        disabled={disabled}
        onClick={onAssign}
      >
        <span aria-hidden="true">+</span> {t(`${TRANSLATION_ROOT}.selection.assign`)}
      </Button>
      <Button
        className="endpoints-selection-action"
        variant="default"
        size="sm"
        disabled={disabled}
        loading={removing}
        onClick={onRemove}
      >
        <span aria-hidden="true">−</span> {t(`${TRANSLATION_ROOT}.selection.remove`)}
      </Button>
      <Button
        className="endpoints-selection-deselect"
        variant="link"
        size="sm"
        onClick={onDeselect}
      >
        {t(`${TRANSLATION_ROOT}.selection.deselect-all`)}
      </Button>
    </fieldset>
  );
};

function EndpointsSection() {
  const { t } = useTranslation();
  const { emitError } = useErrorContext();

  const [searchInput, setSearchInput] = useState('');
  const [searchPattern, setSearchPattern] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [removeFailure, setRemoveFailure] = useState<FailedAssign | null>(null);
  const [overlay, setOverlay] = useState<OverlayKind>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();
  const [announce, statusRegion] = useStatusAnnouncer();

  const {
    endpointDevices,
    totalCount,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useEndpointsList({ searchPattern, sortField: SORT_FIELD_MAP[sortKey], pageSize: PAGE_SIZE });

  useEffect(() => {
    if (isError && error) {
      emitError(new AppError(error));
    }
  }, [isError, error, emitError]);

  useEffect(() => () => clearTimeout(searchTimer.current), []);

  const { groups, assign } = useEndpointGroups();
  const escalate = useSessionErrorEscalation();
  useEmitOnError(isSessionError(groups.error) ? groups.error : null);
  const { manualGroups, activeFilter, filterOptions, activeFilterKey, selectFilter } =
    useGroupFilter(groups.data);

  const fallbackLabel = t(`${TRANSLATION_ROOT}.fallback.unregistered`);
  const rows = useMemo(
    () =>
      endpointDevices
        .map(device =>
          toEndpointRow(
            device,
            fallbackLabel,
            date => t(`${TRANSLATION_ROOT}.columns.installed-date`, { date }),
            manualGroups,
          ),
        )
        .filter(row => matchesGroupFilter(row.group, activeFilter)),
    [endpointDevices, fallbackLabel, t, manualGroups, activeFilter],
  );

  const changeSelection = (next: readonly string[]) => {
    setSelected(next);
    setRemoveFailure(null);
    announce(t(`${TRANSLATION_ROOT}.selection.count`, { count: next.length, total: shownCount }));
  };
  const clearSelection = () => {
    setSelected([]);
    setRemoveFailure(null);
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    clearSelection();
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearchPattern(value.trim());
    }, SEARCH_DEBOUNCE_MS);
  };

  const visibleKeys = rows.map(row => row.rowKey);
  const selectAllKeys = visibleKeys.slice(0, SELECT_ALL_MAX);
  const selectedVisible = visibleKeys.filter(key => selected.includes(key)).length;
  const allSelected =
    selectAllKeys.length > 0 && selectAllKeys.every(key => selected.includes(key));

  const handleRemoveFromGroup = () => {
    setRemoveFailure(null);
    assign.mutate(
      { groupId: null, endpointUuids: selected },
      {
        onSuccess: outcome => {
          if (outcome.kind === 'done') {
            clearSelection();
            announce(t(`${TRANSLATION_ROOT}.groups.announce.removed`, { count: outcome.changed }));
            return;
          }
          escalate(outcome.error);
          setRemoveFailure(outcome);
        },
      },
    );
  };

  const handleAssigned = (count: number, groupName: string) => {
    clearSelection();
    announce(t(`${TRANSLATION_ROOT}.groups.announce.assigned`, { count, name: groupName }));
  };

  const isEmpty = !isLoading && !isError && rows.length === 0;
  const { morePages, shownCount, showEmptyState, spinning } = listProgress({
    activeFilter,
    searchPattern,
    groups: groups.data,
    loadedMatches: rows.length,
    totalCount,
    hasNextPage: Boolean(hasNextPage),
    isFetchingNextPage,
    isEmpty,
  });

  // Infinite scroll (am-portal standard; mirrors Users): fetch the next page as
  // a sentinel below the list scrolls into view.
  // A callback ref in state, so the observer moves to a new sentinel when an empty state (no rows
  // and no more pages) unmounts it and it comes back.
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinel;
    if (!node) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && morePages && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: '400px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [sentinel, fetchNextPage, morePages, isFetchingNextPage]);

  // Reuse the existing endpoint-data report pipeline for export (REQ-050) — no new export logic.
  const { mutateAsync: exportEndpoints, isPending: isExporting } = useAgentVersionReport();
  const handleExport = async () => {
    try {
      const blob = await exportEndpoints();
      downloadAsFileToUserComputer(blob, FILES_DOWNLOAD_NAMES.ENDPOINTS_REPORT);
    } catch (e) {
      emitError(new AppError(e as ApiError));
    }
  };

  const hasActiveSearch = searchPattern.length > 0 || activeFilter.kind !== 'all';

  const [headerEl, setHeaderEl] = useState<Element | null>(null);
  useLayoutEffect(() => {
    const header = document.querySelector('.settings-header');
    if (!header) {
      return undefined;
    }
    const container = document.createElement('div');
    container.className = 'endpoints-header-portal';
    header.appendChild(container);
    setHeaderEl(container);
    return () => {
      container.remove();
      setHeaderEl(null);
    };
  }, []);

  const handleFilterChange = (key: string | null) => {
    selectFilter(key);
    clearSelection();
  };

  const sortItems: { value: SortKey; label: string }[] = [
    { value: 'name', label: t(`${TRANSLATION_ROOT}.sort.by-name`) },
    { value: 'most-up-to-date', label: t(`${TRANSLATION_ROOT}.sort.most-up-to-date`) },
    { value: 'least-up-to-date', label: t(`${TRANSLATION_ROOT}.sort.least-up-to-date`) },
    {
      value: 'most-recently-installed',
      label: t(`${TRANSLATION_ROOT}.sort.most-recently-installed`),
    },
    { value: 'oldest-installed', label: t(`${TRANSLATION_ROOT}.sort.oldest-installed`) },
  ];

  return (
    <>
      {/* First, not last: the region is absolutely positioned with no positioned ancestor inside
          the .settings-content scroller, so after a long list it would stretch the whole page and
          make it scroll. */}
      {statusRegion}
      {headerEl &&
        createPortal(
          <div className="endpoints-header-actions">
            <Button
              variant="default"
              icon={<Settings size={16} />}
              onClick={() => setOverlay('installer')}
            >
              {t(`${TRANSLATION_ROOT}.actions.agent-settings`)}
            </Button>
            <Button
              variant="default"
              icon={<Waypoints size={16} />}
              onClick={() => setOverlay('behavior')}
            >
              {t(`${TRANSLATION_ROOT}.actions.endpoint-behavior`)}
            </Button>
            <Button
              variant="default"
              icon={<GroupIcon aria-hidden="true" width={16} height={16} />}
              onClick={() => setOverlay('manage-groups')}
            >
              {t(`${TRANSLATION_ROOT}.actions.manage-groups`)}
            </Button>
            <Button
              variant="default"
              icon={<LogOut size={16} />}
              disabled={isExporting}
              onClick={handleExport}
            >
              {t(`${TRANSLATION_ROOT}.actions.export`)}
            </Button>
          </div>,
          headerEl,
        )}

      <div className="endpoints-container">
        {/* Search, filters, the Assign bar and its error stay put while the rows scroll. */}
        <div className="endpoints-sticky">
          <div className="endpoints-toolbar">
            {/* antd, like the row checkboxes the Table renders, so the two match. */}
            <Checkbox
              className="endpoints-select-all"
              aria-label={t(`${TRANSLATION_ROOT}.selection.select-all`)}
              checked={allSelected}
              indeterminate={selectedVisible > 0 && !allSelected}
              disabled={visibleKeys.length === 0}
              onChange={() => changeSelection(allSelected ? [] : selectAllKeys)}
            />
            <SearchInput
              placeholder={t(`${TRANSLATION_ROOT}.search-placeholder`)}
              value={searchInput}
              onChange={e => handleSearchChange(e.target.value)}
            />
            <div className="endpoints-toolbar-controls">
              {/* A long group name is truncated (Endpoints.less); the tooltip shows it in full. */}
              <div
                className="endpoints-group-filter"
                title={filterOptions.find(option => option.key === activeFilterKey)?.label}
              >
                <Select
                  variant="tertiary"
                  inputSize="sm"
                  aria-label={t(`${TRANSLATION_ROOT}.groups.filter.label`)}
                  items={filterOptions.map(({ key, label }) => ({ value: key, label }))}
                  value={activeFilterKey}
                  onSelectionChange={handleFilterChange}
                />
              </div>
              <div className="endpoints-sort">
                <Select
                  variant="tertiary"
                  inputSize="sm"
                  aria-label={t(`${TRANSLATION_ROOT}.sort.label`)}
                  items={sortItems}
                  value={sortKey}
                  onSelectionChange={value => {
                    setSortKey(value as SortKey);
                    clearSelection();
                  }}
                />
              </div>
              <span className="endpoints-count">
                <Trans t={t} i18nKey={`${TRANSLATION_ROOT}.count`} count={shownCount} />
              </span>
            </div>
          </div>

          {selected.length > 0 && (
            <SelectionBar
              count={selected.length}
              total={shownCount}
              removing={assign.isPending && overlay !== 'assign'}
              disabled={assign.isPending}
              onAssign={() => setOverlay('assign')}
              onRemove={handleRemoveFromGroup}
              onDeselect={clearSelection}
            />
          )}
          {removeFailure && (
            <Alert
              status="error"
              title={t(`${TRANSLATION_ROOT}.groups.errors.remove-failed`)}
              message={
                removeFailure.changed > 0
                  ? t(`${TRANSLATION_ROOT}.groups.errors.remove-partial`, {
                      changed: removeFailure.changed,
                      total: removeFailure.total,
                    })
                  : undefined
              }
              onClose={() => setRemoveFailure(null)}
            />
          )}
        </div>

        {showEmptyState && hasActiveSearch && <EndpointsNoResults />}
        {showEmptyState && !hasActiveSearch && (
          <EndpointsEmpty onOpenSettings={() => setOverlay('installer')} />
        )}
        {!isEmpty && (
          <Table<ReturnType<typeof toEndpointRow>>
            className="endpoints-table"
            dataSource={rows}
            rowKey="rowKey"
            loading={isLoading}
            pagination={false}
            showHeader={false}
            columns={buildColumns(t)}
            rowSelection={buildRowSelection(t, selected, changeSelection)}
          />
        )}
        {!showEmptyState && (
          <div ref={setSentinel} className="endpoints-scroll-sentinel">
            {spinning && <Spin size="small" />}
          </div>
        )}
      </div>

      {/* Drawer open/close is local state, so dismissing never touches list state (REQ-036/042). */}
      <AgentInstallerDrawer open={overlay === 'installer'} onClose={() => setOverlay(null)} />
      <EndpointBehaviorDrawer open={overlay === 'behavior'} onClose={() => setOverlay(null)} />
      <ManageGroupsDialog
        open={overlay === 'manage-groups'}
        onClose={() => setOverlay(null)}
        announce={announce}
      />
      <AssignGroupDialog
        open={overlay === 'assign'}
        endpointUuids={selected}
        onClose={() => setOverlay(null)}
        onAssigned={handleAssigned}
      />
    </>
  );
}

function EndpointsGate() {
  const { tenantInfo } = useSession();
  if (!hasEntitlement(tenantInfo, Entitlement.SSO)) {
    return null;
  }
  return <EndpointsSection />;
}

export default withRole({ authorizedRoles: [ROLE_TYPE_ADMIN] })(EndpointsGate);
